import React from 'react';
import { useTheme, type ThemePref } from '../context/ThemeContext';

/**
 * Theme toggle icon button. Cycles light -> dark -> system on click.
 *
 * Icon reflects the CURRENT preference (sun=light, moon=dark, monitor=system).
 * aria-label describes the NEXT state for clearer screen-reader announcement.
 *
 * Uses semantic Tailwind classes only (no slate-* literals), so it works
 * in both light and dark modes without adjustment.
 */

const NEXT_LABEL: Record<ThemePref, string> = {
  light: 'Switch to dark theme',
  dark: 'Switch to system theme',
  system: 'Switch to light theme',
};

const TITLE: Record<ThemePref, string> = {
  light: 'Theme: Light',
  dark: 'Theme: Dark',
  system: 'Theme: System',
};

function SunIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  );
}

export function ThemeToggle({ className = '' }: { className?: string }) {
  const { preference, cyclePreference } = useTheme();

  const Icon =
    preference === 'light' ? SunIcon : preference === 'dark' ? MoonIcon : MonitorIcon;

  return (
    <button
      type="button"
      onClick={cyclePreference}
      aria-label={NEXT_LABEL[preference]}
      title={TITLE[preference]}
      className={
        'inline-flex items-center justify-center min-h-11 min-w-11 p-2 rounded-md ' +
        'text-fg-secondary hover:text-fg-primary hover:bg-bg-elevated ' +
        'transition-colors focus-visible:outline-none ' +
        className
      }
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}

export default ThemeToggle;
